import { AccessToken, OktaAuth, TokenParams } from "@okta/okta-auth-js";

const oauthIssuer = process.env.REACT_APP_OIDC_ISSUER!;
const oauthClientId = process.env.REACT_APP_OIDC_CLIENTID!;

const oktaAuth = new OktaAuth({
  issuer: oauthIssuer,
  clientId: oauthClientId,
  redirectUri: `${document.location.protocol}//${document.location.hostname}${document.location.port === "3000" ? ":3000" : ""}/`,
  postLogoutRedirectUri: window.location.origin,
  restoreOriginalUri: async (oktaAuth, originalUri) => {
    document.location.replace(originalUri ?? "/");
  },

  cookies: {
    secure: true,
    path: "/",
    sameSite: "strict",
    expires: new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000),
  },

  storageManager: {
    token: {
      storageType: "cookie",
      useSeparateCookies: false,
      storageKey: "MRLDOCS",
    },
  },
});

export async function getJWT() {
  // Handle any pending login redirects
  const tokenManager = oktaAuth.tokenManager;
  // Attempt to get the access token
  let accessToken = await getAccessToken(tokenManager);

  // If the token has expired, renew it
  if (tokenManager.hasExpired(accessToken)) {
    accessToken = await renewAccessToken(tokenManager);
  }
  // Return the access token and the subject claim
  return accessToken?.accessToken;
}

export async function getBothJWTs() {
  const tokenManager = oktaAuth.tokenManager;
  // Attempt to get the access token
  let accessToken = await getAccessToken(tokenManager);

  // If the token has expired, renew it
  if (tokenManager.hasExpired(accessToken)) {
    accessToken = await renewAccessToken(tokenManager);
  }
  // Return the access token and the subject claim
  return [accessToken?.accessToken, oktaAuth.getRefreshToken()];
}

async function processRedirect() {
  try {
    await oktaAuth.handleRedirect(oktaAuth.getOriginalUri());
    console.log("Redirect handled");
  } catch (ex) {
    console.log("Redirect fail", ex);
    // simply take them back to the app to start again.
    document.location.replace("/");
  }
}

export function handleLoginRedirect() {
  // Check if this is a login redirect
  if (oktaAuth.token.isLoginRedirect()) {
    processRedirect();
    return true;
  } else {
    return false;
  }
}

async function getAccessToken(tokenManager: any): Promise<AccessToken> {
  // Attempt to get the access token from the token manager
  let accessToken = (await tokenManager.get("accessToken")) as AccessToken;
  if (!accessToken) {
    // If no access token (caused by it being deleted by tokenManager.renew(), which deletes tokens even if it cannot fetch a new one due to network error),
    //try to get a new one using the refresh token
    accessToken = await handleMissingAccessToken(tokenManager);
  }
  return accessToken;
}

async function handleMissingAccessToken(tokenManager: any): Promise<AccessToken> {
  // Try to get the refresh token
  const refreshToken = (await tokenManager.get("refreshToken")) as AccessToken;
  if (!refreshToken) {
    console.log("No Refresh token");
    throw new Error("Unauthorised access");
  }

  try {
    // Use the refresh token to get a new access token
    const newAccessToken = await renewAccessToken(tokenManager);
    return newAccessToken;
  } catch (error: any) {
    // Propagate any errors
    throw error;
  }
}

async function renewAccessToken(tokenManager: any): Promise<AccessToken> {
  console.log("Renewing Access token");
  try {
    // Attempt to renew the access token
    return (await tokenManager.renew("accessToken")) as AccessToken;
  } catch (error: any) {
    // Handle network errors specifically
    if (error.message === "Failed to fetch") {
      //This error is handled by DocListSynchroniser
      throw new Error("Network error renewing access token");
    }
    // Propagate other errors
    throw error;
  }
}

export function doSignIn(returnUrl?: string) {
  const tokenParams: TokenParams = {
    scopes: ["openid", "email", "offline_access"],
  };
  if (returnUrl) {
    oktaAuth.setOriginalUri(returnUrl);
  }
  oktaAuth.token.getWithRedirect(tokenParams);
}

export async function doSignOut() {
  console.log("Signing out");
  try {
    // The redirect will be handled by Okta
    await oktaAuth.signOut({
      clearTokensBeforeRedirect: true,
    });
  } catch (error) {
    console.error("Error signing out:", error);
  }
}
